import React, { useLayoutEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Recommendation, TradeSuggestionsItem } from '@/types/client';
import { formatDate, formatNumber } from '@/utils/formatters';
import { Pill, Tag, Placeholder, signedClass } from '@/components/ui';
import { isHandedOnCall } from '@/components/order_recommendation/recommendationSelectors';
import { HeaderLabel } from '@/components/utils/DataTable';
import shared from '@/components/utils/shared.module.css';
import css from './TradeSuggestions.module.css';
import { GroupHeaderCell, layoutHeader, useGroupHeaderRow } from '@/components/utils/groupedHeader';
import type { ColumnGroup } from '@/components/utils/groupedHeader';
import {
  DASH,
  STRATEGY_SORT_SEP,
  formatPrice,
  hasMultipleClients,
  impliedReturnBlankReason,
  isGlobalItem,
  ltpVsRecPrice,
  rowImpliedReturn,
  strategyStats,
  suggestionTone,
  targetForIr,
} from './tradeSuggestionColumns';

/** Buy/sell/mixed drive a cell's background tint — the merged-row restatement
 *  of what the Pill already says in text. */
type RowTone = 'buy' | 'sell' | 'mixed' | 'none';

const toneOfSuggestion = (tradeType: string | null | undefined): RowTone => {
  const tone = suggestionTone(tradeType);
  return tone === 'positive' ? 'buy' : tone === 'critical' ? 'sell' : 'none';
};

/** The trade direction across every strategy shown for one recommendation —
 *  paints the recommendation-level cells (Rec. By through Implied Return) the
 *  way the old table painted the whole rec row. */
const recRowTone = (rec: Recommendation | null, strategyKeys: string[]): RowTone => {
  if (!rec) return 'none';
  let buy = false;
  let sell = false;
  strategyKeys.forEach(code => {
    const t = toneOfSuggestion(rec.suggestions?.[code]?.trade_type);
    if (t === 'buy') buy = true;
    if (t === 'sell') sell = true;
  });
  if (buy && sell) return 'mixed';
  if (buy) return 'buy';
  if (sell) return 'sell';
  return 'none';
};

const toneClass = (tone: RowTone): string | undefined => (
  tone === 'buy' ? css['tone-buy']
    : tone === 'sell' ? css['tone-sell']
      : tone === 'mixed' ? css['tone-mixed']
        : undefined
);

/** A signed figure — colour is the meaning, never decoration. */
const signedPercent = (value: number | null, title?: string): React.ReactNode => {
  if (value == null) return <span title={title}>{DASH}</span>;
  return (
    <span className={signedClass(value) || undefined} title={title}>
      {value > 0 ? '+' : ''}{formatNumber(value, 2)}%
    </span>
  );
};

interface ColSpec {
  key: string;
  header: string;
  tooltip?: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  sortable?: boolean;
  /** Company-level: rendered once per company with a rowSpan, instead of once
   *  per recommendation row — the merged-cell look the old table had. */
  spanCompany?: boolean;
  /** Header group — "Recommendations" or a strategy code. Company/LTP have none
   *  and span both header rows. */
  group?: string;
  /** Pinned on horizontal scroll. Sticky columns must lead the list; their
   *  left offsets are measured from the rendered header cells. */
  sticky?: boolean;
}

export interface TradeSuggestionsTableProps {
  /** The rows to show — already searched and sorted. */
  items: TradeSuggestionsItem[];
  /** The tab's full, unsearched rows. The strategy column set and AUM tooltips
   *  come from these, so typing in search never reshapes the header. Defaults to `items`. */
  columnItems?: TradeSuggestionsItem[];
  strategyKeys: string[];
  sortField: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  loading: boolean;
  loadingMessage?: string;
  emptyMessage: string;
}

/** "CODE · AUM 12.34 Cr · 5 client(s)" — the context every strategy header and
 *  its leaf-column tooltips carry. */
const strategyContext = (code: string, items: TradeSuggestionsItem[]): string => {
  const stats = strategyStats(items, code);
  return `${code} · AUM ${formatNumber(stats.totalAum / 10000000, 2)} Cr · ${stats.totalClients} client(s)`;
};

const alignClass = (align?: 'left' | 'right' | 'center'): string => (
  align === 'right' ? shared['text-right'] : align === 'center' ? shared['text-center'] : ''
);

/**
 * A hand-rolled table, not the shared DataTable: a company's recommendations
 * are merged with a real rowSpan on its own cells, and buy/sell/mixed paint
 * the recommendation cells as a background tint. Both need every row of a
 * company kept together and visible at once, which is what the generic
 * flat-row, paginated DataTable cannot give it — see OrderRecommendationList's
 * table for the same pattern. There is deliberately no pager: a company's
 * rowSpan cannot straddle a page boundary, so the table scrolls instead,
 * exactly like the page did before it moved onto DataTable.
 */
const TradeSuggestionsTable: React.FC<TradeSuggestionsTableProps> = ({
  items,
  columnItems = items,
  strategyKeys,
  sortField,
  sortDir,
  onSort,
  loading,
  loadingMessage = 'Loading trade suggestions…',
  emptyMessage,
}) => {
  const refDate = useMemo(() => new Date(), []);

  const REC_GROUP_KEY = 'recommendations';

  const columns = useMemo<ColSpec[]>(() => {
    const cols: ColSpec[] = [
      { key: 'symbol', header: 'Company', sortable: true, width: '200px', spanCompany: true, sticky: true },
      { key: 'ltp', header: 'LTP', tooltip: 'Last traded price', sortable: true, align: 'right', width: '110px', spanCompany: true, sticky: true },
      // The "Rec." prefix is redundant once these sit under the "Recommendations"
      // group header (row 1) — same reasoning as dropping the strategy code from
      // the Min/Median/Max/Trade headers below.
      { key: 'recommended_by', group: REC_GROUP_KEY, header: 'By', tooltip: 'Who recommended this trade', sortable: true, width: '150px' },
      { key: 'recommendation_type', group: REC_GROUP_KEY, header: 'Type', tooltip: 'Recommendation type', sortable: true, width: '90px' },
      { key: 'decision', group: REC_GROUP_KEY, header: 'Rating', tooltip: 'Recommendation rating', sortable: true, width: '100px' },
      { key: 'decision_price', group: REC_GROUP_KEY, header: 'Price', tooltip: 'The price the recommendation was made at', sortable: true, align: 'right', width: '90px' },
      {
        key: 'cmp_vs_model',
        group: REC_GROUP_KEY,
        align: 'right',
        header: 'vs Rec. Price',
        tooltip: 'Percentage difference between LTP and Recommended Price: ((LTP - Rec. Price) / Rec. Price) × 100',
        sortable: true,
        width: '100px',
      },
      { key: 'rec_date', group: REC_GROUP_KEY, header: 'Date', tooltip: 'Date of the recommendation', sortable: true, width: '90px' },
      { key: 'target_weight', group: REC_GROUP_KEY, header: 'Weight', tooltip: 'Recommended weight percentage', sortable: true, align: 'right', width: '80px' },
      { key: 'target_price', group: REC_GROUP_KEY, header: 'Target Price', sortable: true, align: 'right', width: '100px' },
      {
        key: 'implied_return',
        group: REC_GROUP_KEY,
        align: 'right',
        header: 'Implied Return',
        tooltip: 'Annualised implied return from LTP to Target Price: ((Target Price / LTP) ^ (1 / Years to Target Date)) - 1. '
          + 'Same calculation as the Orders & Recommendations page. Blank when there is no future target date.',
        sortable: true,
        width: '110px',
      },
    ];

    strategyKeys.forEach(code => {
      const context = strategyContext(code, columnItems);

      // Group headers already carry the strategy code, so the leaf columns underneath
      // it are just "Min %"/"Median %"/… — repeating the code in every one of them is
      // what made this table run so wide.
      if (hasMultipleClients(columnItems, code)) {
        cols.push(
          { key: `${code}${STRATEGY_SORT_SEP}min`, group: code, header: 'Min %', tooltip: `Minimum client holding — ${context}`, sortable: true, align: 'right', width: '70px', spanCompany: true },
          { key: `${code}${STRATEGY_SORT_SEP}median`, group: code, header: 'Median %', tooltip: `Median client holding — ${context}`, sortable: true, align: 'right', width: '80px', spanCompany: true },
          { key: `${code}${STRATEGY_SORT_SEP}max`, group: code, header: 'Max %', tooltip: `Maximum client holding — ${context}`, sortable: true, align: 'right', width: '70px', spanCompany: true },
        );
      } else {
        cols.push({ key: `${code}${STRATEGY_SORT_SEP}median`, group: code, header: 'Holding %', tooltip: `Client holding — ${context}`, sortable: true, align: 'right', width: '90px', spanCompany: true });
      }

      cols.push({ key: `${code}${STRATEGY_SORT_SEP}trade`, group: code, header: 'Trade', tooltip: `Trade suggestion for ${context}`, width: '110px' });
    });

    return cols;
  }, [columnItems, strategyKeys]);

  /** Labels for the header groups: "Recommendations", and each strategy code
   *  with its AUM and client count as a tooltip. */
  const groups = useMemo<ColumnGroup[]>(() => [
    { key: REC_GROUP_KEY, label: 'Recommendations' },
    ...strategyKeys.map(code => ({ key: code, label: code, tooltip: strategyContext(code, columnItems) })),
  ], [strategyKeys, columnItems]);

  const headerLayout = useMemo(() => layoutHeader(columns, groups), [columns, groups]);

  const stickyKeys = useMemo(() => {
    const keys: string[] = [];
    for (const col of columns) {
      if (!col.sticky) break;
      keys.push(col.key);
    }
    return keys;
  }, [columns]);
  const lastStickyKey = stickyKeys[stickyKeys.length - 1];
  const { tableRef, groupRowRef } = useGroupHeaderRow(headerLayout.grouped);

  /** Left offset of each sticky column, measured from the rendered header.
   *  The table keeps auto layout, so a `width` is only a minimum — a column
   *  grows to fit its header, and a summed offset would leave LTP overlapping
   *  a widened Company column. */
  const [stickyLeft, setStickyLeft] = useState<number[]>([]);
  useLayoutEffect(() => {
    const tableEl = tableRef.current;
    if (!tableEl || stickyKeys.length === 0) return undefined;
    const cells = stickyKeys
      .map(key => tableEl.querySelector<HTMLTableCellElement>(`thead th[data-sticky="${key}"]`))
      .filter((el): el is HTMLTableCellElement => el !== null);
    const measure = () => {
      const offsets: number[] = [];
      let acc = 0;
      cells.forEach(el => {
        offsets.push(acc);
        acc += el.getBoundingClientRect().width;
      });
      setStickyLeft(prev => (
        prev.length === offsets.length && prev.every((v, i) => v === offsets[i]) ? prev : offsets
      ));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    cells.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [stickyKeys, tableRef]);

  const stickyStyle = (col: ColSpec, zIndex: number): React.CSSProperties | undefined => {
    const index = stickyKeys.indexOf(col.key);
    if (index === -1) return undefined;
    const left = stickyLeft[index] ?? 0;
    return {
      position: 'sticky',
      left: `${left}px`,
      zIndex,
      ...(col.key === lastStickyKey ? { boxShadow: 'var(--shadow-pin)' } : {}),
    };
  };

  /** A leaf header cell — `span` for Company/LTP, which have no group and fill
   *  both header rows. As in DataTable, a right-aligned column puts its sort
   *  icon and ⓘ before the label so the label lines up with the figures. */
  const renderHeaderCell = (col: ColSpec, row?: 'span') => {
    const isActive = sortField === col.key;
    const thClass = [
      alignClass(col.align),
      col.sortable ? shared['th-sortable'] : '',
      col.sortable && isActive ? shared['th-active'] : '',
    ].filter(Boolean).join(' ');
    return (
      <th
        key={col.key}
        rowSpan={row === 'span' ? 2 : undefined}
        data-sticky={col.sticky ? col.key : undefined}
        className={thClass || undefined}
        style={{
          ...(col.width ? { width: col.width } : {}),
          // Above shared.module.css's z-index: 10 on the other head cells — the
          // "Recommendations"/strategy group cells included — so none slide over.
          ...stickyStyle(col, 11),
          ...(col.sticky ? { top: 0, backgroundColor: 'var(--tbl-head-bg)' } : {}),
        }}
        onClick={col.sortable ? () => onSort(col.key) : undefined}
      >
        <HeaderLabel
          header={col.header}
          tooltip={col.tooltip}
          align={col.align}
          sortable={col.sortable}
          active={isActive}
          sortDir={sortDir}
        />
      </th>
    );
  };

  /** Company-level cell content — read from `item` alone, independent of which
   *  recommendation row is currently being rendered. */
  const renderCompanyCell = (col: ColSpec, item: TradeSuggestionsItem): React.ReactNode => {
    switch (col.key) {
      case 'symbol':
        return (
          <span className={css['company-cell']}>
            <Link to={`/trade-suggestions/${item.isin_number}`} className={css['symbol-link']} title={item.company_name}>
              {item.symbol}
            </Link>
            {(item.is_invested || item.category) && (
              <span className={css['company-tags']}>
                {item.is_invested && <Pill tone="info">Invested</Pill>}
                {item.category && <Tag>{item.category}</Tag>}
              </span>
            )}
          </span>
        );
      case 'ltp':
        return (
          <span title={item.ltp_timestamp ? `LTP updated: ${formatDate(item.ltp_timestamp)}` : undefined}>
            {item.ltp != null ? formatPrice(item.ltp, item.ltp_currency_code, isGlobalItem(item)) : DASH}
          </span>
        );
      default: {
        if (!col.key.includes(STRATEGY_SORT_SEP)) return DASH;
        const [code, holdingType] = col.key.split(STRATEGY_SORT_SEP);
        const strategy = item.strategies[code];
        if (!strategy) return DASH;
        const value = holdingType === 'min' ? strategy.min_client_holding
          : holdingType === 'max' ? strategy.max_client_holding
            : strategy.median_client_holding;
        return `${formatNumber(value, 2)}%`;
      }
    }
  };

  /** Recommendation-level cell content — one recommendation's own figures, or
   *  a strategy's trade suggestion for it. */
  const renderRecCell = (col: ColSpec, item: TradeSuggestionsItem, rec: Recommendation | null): React.ReactNode => {
    const isGlobal = isGlobalItem(item);
    switch (col.key) {
      case 'recommended_by': {
        if (!rec?.recommended_by) return DASH;
        const role = rec.recommended_by === item.latest_rec_analyst_name
          ? 'Analyst'
          : rec.recommended_by === item.latest_rec_ic_user_name
            ? 'IC'
            : null;
        // The company has since changed hands: this is still the latest call in that role,
        // but the person who made it no longer holds the company.
        const currentOwner = role === 'Analyst'
          ? item.analyst_name
          : role === 'IC'
            ? item.ic_user_name
            : null;
        const isPrevOwner = isHandedOnCall(rec.recommended_by, currentOwner);
        const title = [rec.estimate_type, rec.comment].filter(Boolean).join(': ');
        const nameClass = role === 'Analyst' ? css['analyst-name'] : role === 'IC' ? css['ic-name'] : undefined;
        return (
          <span className={css['rec-by']} title={title || undefined}>
            <span className={nameClass}>{rec.recommended_by}</span>
            {role && <Tag>{role}</Tag>}
            {/* This screen names the current holder; the Orders & Execution table deliberately
                does not, and only marks that the call was handed on. The difference is
                intentional -- that table is dense and read at a glance. */}
            {isPrevOwner && <Tag title={`No longer the ${role} for this company — current: ${currentOwner}`}>Prev</Tag>}
          </span>
        );
      }
      case 'recommendation_type':
        return rec?.recommendation_type || DASH;
      case 'decision':
        return rec?.decision || DASH;
      case 'decision_price':
        return rec?.decision_price
          ? formatPrice(rec.decision_price, item.currency_code, isGlobal)
          : DASH;
      case 'cmp_vs_model':
        return signedPercent(ltpVsRecPrice(item, rec));
      case 'rec_date':
        return rec?.rec_date ? formatDate(rec.rec_date) : DASH;
      case 'target_weight':
        return rec?.target_weight != null ? `${formatNumber(rec.target_weight, 2)}%` : DASH;
      case 'target_price':
        return (
          <span title={rec?.target_date ? `Target date: ${formatDate(rec.target_date)}` : undefined}>
            {rec?.target_price != null ? formatPrice(rec.target_price, item.currency_code, isGlobal) : DASH}
          </span>
        );
      case 'implied_return': {
        const ir = rowImpliedReturn(item, rec, refDate);
        if (ir == null) return signedPercent(null, impliedReturnBlankReason(item, rec, refDate));
        const target = targetForIr(rec);
        const title = [
          target != null && `Target: ${formatPrice(target, item.currency_code, isGlobal)}`,
          item.ltp != null && `LTP: ${formatPrice(item.ltp, item.ltp_currency_code, isGlobal)}`,
          rec?.target_date && `Target date: ${formatDate(rec.target_date)}`,
        ].filter(Boolean).join(' · ');
        return signedPercent(ir, title);
      }
      default: {
        const [code] = col.key.split(STRATEGY_SORT_SEP);
        const suggestion = rec?.suggestions?.[code];
        if (!suggestion || (!suggestion.trade_type && !suggestion.trade_description)) return DASH;
        return (
          <Pill tone={suggestionTone(suggestion.trade_type)} title={suggestion.trade_description || undefined}>
            {suggestion.trade_type || suggestion.trade_description}
          </Pill>
        );
      }
    }
  };

  return (
      <div className={shared['table-scroll']}>
        <table ref={tableRef} className={`${shared.table} ${shared['table-compact']}`}>
          <thead>
            <tr ref={groupRowRef}>
              {headerLayout.top.map(cell => (
                cell.kind === 'column'
                  ? renderHeaderCell(cell.column, 'span')
                  : <GroupHeaderCell key={cell.key} group={cell.group} span={cell.span} />
              ))}
            </tr>
            <tr className={shared['sub-header-row']}>
              {headerLayout.sub.map(({ column }) => renderHeaderCell(column))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length}>
                  <Placeholder kind="loading" message={loadingMessage} />
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <Placeholder message={emptyMessage} />
                </td>
              </tr>
            ) : (
              items.flatMap(item => {
                const rowCount = Math.max(1, item.recommendations.length);
                return Array.from({ length: rowCount }, (_, recIndex) => {
                  const rec = item.recommendations[recIndex] ?? null;
                  const isFirst = recIndex === 0;
                  const tone = recRowTone(rec, strategyKeys);

                  return (
                    <tr key={`${item.isin_number}-${recIndex}`} className={isFirst ? css['group-first'] : undefined}>
                      {columns.map(col => {
                        if (col.spanCompany) {
                          if (!isFirst) return null;
                          return (
                            <td key={col.key} rowSpan={rowCount} className={alignClass(col.align) || undefined} style={stickyStyle(col, 2)}>
                              {renderCompanyCell(col, item)}
                            </td>
                          );
                        }

                        const isTradeCol = col.key.endsWith(`${STRATEGY_SORT_SEP}trade`);
                        const cellTone = isTradeCol
                          ? toneOfSuggestion(rec?.suggestions?.[col.key.split(STRATEGY_SORT_SEP)[0]]?.trade_type)
                          : tone;

                        return (
                          <td
                            key={col.key}
                            className={`${alignClass(col.align)} ${toneClass(cellTone) ?? ''}`.trim() || undefined}
                          >
                            {renderRecCell(col, item, rec)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                });
              })
            )}
          </tbody>
        </table>
      </div>
  );
};

export default TradeSuggestionsTable;
